/**
 * Campaign link utilities for banner/carousel ads
 * Generates deep links for click actions
 */

import type { BannerAction } from '../stores/campaign-wizard.store';

// ============ Link Generation ============

/**
 * Generate a banner link based on the action type
 * Links follow the app's deep link format for proper routing
 */
export function generateBannerLink(
  actionType: 'product' | 'shop',
  shopRefId: string,
  productRefId?: string
): string {
  if (actionType === 'product' && productRefId) {
    return `/product/${productRefId}`;
  }
  return `/shop/${shopRefId}`;
}

/**
 * Generate banner link from BannerAction object
 */
export function generateBannerLinkFromAction(
  action: BannerAction,
  shopRefId: string
): string {
  return generateBannerLink(action.type, shopRefId, action.productRefId);
}

// ============ Link Parsing ============

interface ParsedLink {
  type: 'product' | 'shop' | 'unknown';
  refId?: string;
}

/**
 * Parse a banner link to extract the action type and reference ID
 */
export function parseBannerLink(link: string): ParsedLink {
  if (!link) {
    return { type: 'unknown' };
  }

  // Match product links: /product/{refId}
  const productMatch = link.match(/^\/product\/([a-zA-Z0-9_-]+)$/);
  if (productMatch) {
    return { type: 'product', refId: productMatch[1] };
  }

  // Match shop links: /shop/{refId} or /shops/{refId}
  const shopMatch = link.match(/^\/shops?\/([a-zA-Z0-9_-]+)$/);
  if (shopMatch) {
    return { type: 'shop', refId: shopMatch[1] };
  }

  return { type: 'unknown' };
}

/**
 * Convert a parsed link back to BannerAction
 */
export function parsedLinkToBannerAction(parsed: ParsedLink): BannerAction {
  if (parsed.type === 'product' && parsed.refId) {
    return { type: 'product', productRefId: parsed.refId };
  }
  return { type: 'shop' };
}

// ============ Link Validation ============

/**
 * Validate if a banner link is in the correct format
 */
export function isValidBannerLink(link: string): boolean {
  const parsed = parseBannerLink(link);
  return parsed.type !== 'unknown';
}

// ============ Display Helpers ============

/**
 * Get a human-readable description of a banner action
 */
export function getBannerActionDescription(
  action: BannerAction,
  productName?: string,
  shopName?: string
): string {
  if (action.type === 'product') {
    return productName
      ? `Redirection vers le produit "${productName}"`
      : 'Redirection vers un produit';
  }
  return shopName
    ? `Redirection vers la boutique "${shopName}"`
    : 'Redirection vers votre boutique';
}

/**
 * Get a short label for the action type
 */
export function getBannerActionLabel(actionType: 'product' | 'shop'): string {
  return actionType === 'product' ? 'Produit' : 'Boutique';
}
